import { getRemoteConfig, fetchAndActivate, getString } from 'firebase/remote-config';
import { app } from './firebase';

export interface UserRole {
  email: string;
  role: 'admin' | 'gestore' | 'operatore';
}

let cachedUsersRole: UserRole[] | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 60 * 1000; // 1 minuto di cache

/**
 * Recupera la configurazione degli utenti e ruoli da Firebase Remote Config
 */
export async function getUsersRoleFromRemoteConfig(): Promise<UserRole[]> {
  // Usa cache se disponibile e non scaduta
  const now = Date.now();
  if (cachedUsersRole && (now - lastFetchTime) < CACHE_DURATION) {
    return cachedUsersRole;
  }

  try {
    const remoteConfig = getRemoteConfig(app);

    // Imposta configurazione: fetch ogni 1 minuto
    remoteConfig.settings.minimumFetchIntervalMillis = 60000; // 1 minuto

    // Fetch e attiva i nuovi valori
    await fetchAndActivate(remoteConfig);

    // Recupera il valore della Remote Config "usersRole"
    const usersRoleString = getString(remoteConfig, 'usersRole');

    if (!usersRoleString) {
      console.warn('[RemoteConfig] usersRole non configurato, uso default vuoto');
      return [];
    }

    // Parse JSON
    const usersRole: UserRole[] = JSON.parse(usersRoleString);

    // Valida struttura
    if (!Array.isArray(usersRole)) {
      console.error('[RemoteConfig] usersRole non è un array valido');
      return [];
    }

    // Aggiorna cache
    cachedUsersRole = usersRole;
    lastFetchTime = now;

    console.log('[RemoteConfig] Caricati', usersRole.length, 'utenti da Remote Config');
    return usersRole;

  } catch (error) {
    console.error('[RemoteConfig] Errore nel recupero usersRole:', error);

    // Se c'è errore ma abbiamo cache, usiamo quella
    if (cachedUsersRole) {
      console.warn('[RemoteConfig] Uso cache precedente dopo errore');
      return cachedUsersRole;
    }

    // Fallback: array vuoto
    return [];
  }
}

/**
 * Ottiene il ruolo di un utente dato il suo email
 */
export async function getUserRole(email: string): Promise<'admin' | 'gestore' | 'operatore' | null> {
  const usersRole = await getUsersRoleFromRemoteConfig();
  const user = usersRole.find(u => u.email.toLowerCase() === email.toLowerCase());
  return user?.role || null;
}

/**
 * Verifica se un utente è admin
 */
export async function isUserAdmin(email: string): Promise<boolean> {
  const role = await getUserRole(email);
  return role === 'admin';
}

/**
 * Verifica se un utente è gestore
 */
export async function isUserGestore(email: string): Promise<boolean> {
  const role = await getUserRole(email);
  return role === 'gestore';
}

/**
 * Verifica se un utente ha privilegi (admin o gestore)
 */
export async function isUserPrivileged(email: string): Promise<boolean> {
  const role = await getUserRole(email);
  return role === 'admin' || role === 'gestore';
}

/**
 * Pulisce la cache (utile per test o dopo modifiche manuali)
 */
export function clearRemoteConfigCache(): void {
  cachedUsersRole = null;
  lastFetchTime = 0;
}
